import {
  DealerDeployment,
  DeploymentTenantLicensePool,
  GlobalSupplier,
  ResultFullSupplierNetwork,
  SupplierInstance,
  SupplierInstanceType,
  Tenant,
  TenantDealerDeploymentConfig
} from '../api/api-interface-licenseserver';
import { Snippet } from '../../webmodule-common/interop/types/misc';
import { html } from 'lit';

export interface DealerDeploymentTenantFlatView {
  dealerDeployment: DealerDeployment;
  tenantConfig: TenantDealerDeploymentConfig;
  tenant: Tenant;
}

interface V6VersionBreakdown {
  macroVersion: string;
  dbInstanceVersion: string;
  dbPricingVersion: string;
  v6Version: string;
}

export interface DealerDeploymentSupplierFlatView {
  dealerDeployment: DealerDeployment;
  supplierInstance: SupplierInstance;
  globalSupplier: GlobalSupplier;
  licensePool: DeploymentTenantLicensePool;
  tenantConfig: TenantDealerDeploymentConfig[];
  supplierInstanceInformation: { [key: string]: string };
  deploymentInformation: { [key: string]: string };
  v6VersionBreakdown: V6VersionBreakdown;
}

export class DataContainer {
  data: ResultFullSupplierNetwork;

  constructor(data: ResultFullSupplierNetwork) {
    this.data = data;
  }

  private parts(val: string[], from, count: number): string {
    //Supplier Ver "10.0.13.31.30.380481012"
    return val.slice(from, from + count).join('.');
  }

  private versionStringToData(version: string): V6VersionBreakdown {
    const nums = version.split('.');

    function ConvertToRealV6Build(version: string) {
      const parts = [version.slice(0, 1), version.slice(1, 3), version.slice(3, 5), version.slice(5)];
      return parts.join('.');
    }

    return {
      macroVersion: this.parts(nums, 0, 3),
      dbInstanceVersion: this.parts(nums, 3, 1),
      dbPricingVersion: this.parts(nums, 4, 1),
      v6Version: ConvertToRealV6Build(this.parts(nums, 5, 1))
    };
  }

  protected getCaption(): Snippet {
    return html`Supplier Deployments`;
  }

  public sumLicenseAllocations(tenantConfig: TenantDealerDeploymentConfig[]): number {
    return tenantConfig.reduce((sum, config) => sum + config.maxLicensePoolAllocation, 0);
  }

  flatTenantView(deploymentId: number, globalSupplierId?: number): DealerDeploymentTenantFlatView[] {
    const dealerDeployment = this.data.dealerDeployments.find(dd => dd.id === deploymentId);
    if (!dealerDeployment) return [];

    const tenantConfigs = this.data.tenantDealerDeploymentConfigs.filter(
      x => x.dealerDeploymentId === dealerDeployment.id
    );
    return tenantConfigs
      .filter(
        x => globalSupplierId === undefined || x.details.supplierReferences.some(y => y.supplierId === globalSupplierId)
      )
      .map(tenantConfig => {
        const tenant = this.data.tenants.find(t => t.id === tenantConfig.tenantId);
        return { dealerDeployment, tenantConfig, tenant: tenant! };
      });
  }

  flatDataView(): DealerDeploymentSupplierFlatView[] {
    const si = this.data.supplierInstances.filter(x => x.instanceType === SupplierInstanceType.DealerConfigurator);
    const flatView: DealerDeploymentSupplierFlatView[] = [];

    for (const supplierInstance of si) {
      const dealerDeployment = this.data.dealerDeployments.find(dd => dd.id === supplierInstance.dealerDeploymentId);
      const globalSupplier = this.data.globalSuppliers.find(gs => gs.id === supplierInstance.globalSupplierId);

      if (dealerDeployment && globalSupplier) {
        flatView.push({
          dealerDeployment,
          supplierInstance,
          globalSupplier,
          licensePool: this.data.deploymentTenantLicensePools.find(d => d.dealerDeploymentId === dealerDeployment.id)!,
          tenantConfig: this.data.tenantDealerDeploymentConfigs.filter(
            x => x.dealerDeploymentId === dealerDeployment.id
          ),
          supplierInstanceInformation: this.data.supplierInstanceInformation[supplierInstance.id] ?? {},
          deploymentInformation: this.data.deploymentInformation[dealerDeployment.id] ?? {},
          v6VersionBreakdown: this.versionStringToData(
            this.data.supplierInstanceInformation[supplierInstance.id]?.['version'] ?? ''
          )
        });
      }
    }
    return flatView.sort((a, b) => {
      const nameComparison = a.globalSupplier.displayName.localeCompare(b.globalSupplier.displayName);
      if (nameComparison !== 0) {
        return nameComparison;
      }
      //reverse order to get the highest operation type first
      const operationComparison = b.dealerDeployment.operationType - a.dealerDeployment.operationType;
      if (operationComparison !== 0) {
        return operationComparison;
      }
      const deploymentTypeComparison = a.dealerDeployment.deploymentType - b.dealerDeployment.deploymentType;
      return deploymentTypeComparison;
    });
  }
}
